/**
 * Main Application Component
 *
 * Provides routing, layout, and authentication for the Cohort Manager application.
 * Uses Cognito + Amazon Federate for SSO authentication.
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { AppLayoutToolbar, TopNavigation, BreadcrumbGroup, Spinner, Box, Button, SpaceBetween } from '@cloudscape-design/components';
import { QueryClientProvider } from '@tanstack/react-query';
import { Amplify } from 'aws-amplify';
import { amplifyConfig } from './config/amplify-config';
import { isMaintenanceMode } from './config/env-vars';

Amplify.configure(amplifyConfig);

// Page imports
import UserDashboard from './pages/UserDashboard';
import TrainingPrograms from './pages/TrainingPrograms';
import ProgramDetails from './pages/ProgramDetails';
import CreateProgram from './pages/ProgramSettings';
import CohortSettings from './pages/CohortSettings';
import CohortDetails from './pages/CohortDetails';
import NotificationsPage from './pages/NotificationsPage';

// Component imports
import { ErrorBoundary, NotificationProvider, NotificationFlashbar } from './components';
import { ProtectedRoute } from './components/ProtectedRoute';

// Context imports
import { BreadcrumbProvider, useBreadcrumbs } from './contexts';

// React Query
import { queryClient } from './lib/queryClient';

// Auth
import { useAuthStore } from './stores/authStore';
import { useCanCreateProgram } from './hooks/usePermissions';
import logo from './assets/cohort.png';
import { useUserPermissions } from './hooks/useUserPermissions';
import { useNotifications } from './hooks/queries/useNotifications';

/**
 * Top Navigation Bar
 */
const TopNav: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const { data: notificationsData } = useNotifications(!!user);
  const hasUnread = (notificationsData?.unreadCount ?? 0) > 0;

  useUserPermissions(user?.userId);

  return (
    <TopNavigation
      identity={{
        href: '/',
        onFollow: (e) => { e.preventDefault(); navigate('/'); },
        title: '',
        logo: {
          src: logo,
          alt: 'Cohort'
        },
      }}
      i18nStrings={{
        overflowMenuTriggerText: 'More',
        overflowMenuTitleText: 'All',
      }}
      utilities={[
        {
          type: 'button',
          text: 'Dashboard',
          href: '/',
          onFollow: (e) => { e.preventDefault(); navigate('/'); },
        },
        {
          type: 'button',
          text: 'Programs',
          href: '/programs',
          onFollow: (e) => { e.preventDefault(); navigate('/programs'); },
        },
        {
          type: 'button',
          iconName: 'support',
          title: 'Help & Wiki',
          ariaLabel: 'Help & Wiki',
          href: 'https://w.amazon.com/bin/view/Users/mmssym/CohortManager/',
          onFollow: (e) => { e.preventDefault(); window.open('https://w.amazon.com/bin/view/Users/mmssym/CohortManager/', '_blank', 'noopener,noreferrer'); },
        },
        {
          type: 'button',
          iconName: 'notification',
          title: 'Notifications',
          ariaLabel: hasUnread ? 'Notifications (unread)' : 'Notifications',
          badge: hasUnread,
          disableUtilityCollapse: false,
          href: '/notifications',
          onFollow: (e) => { e.preventDefault(); navigate('/notifications'); },
        },
        {
          type: 'menu-dropdown',
          text: user?.name || '',
          iconName: 'user-profile',
          items: [{ id: 'signout', text: 'Sign out' }],
          onItemClick: ({ detail }) => {
            if (detail.id === 'signout') logout();
          },
        },
      ]}
    />
  );
};

/**
 * App Content with Router (only rendered when authenticated)
 */
const AppContent: React.FC = () => {
  const user = useAuthStore((state) => state.user);
  const { breadcrumbs } = useBreadcrumbs();
  const canCreateProgram = useCanCreateProgram();

  if (!user) return null;

  return (
    <>
      <TopNav />
      <AppLayoutToolbar
        navigationHide
        toolsHide
        breadcrumbs={
          breadcrumbs.length > 0 ? <BreadcrumbGroup items={breadcrumbs} ariaLabel="Breadcrumbs" /> : undefined
        }
        notifications={<NotificationFlashbar />}
        content={
          <Routes>
            <Route path="/" element={<UserDashboard />} />
            <Route path="/programs" element={<TrainingPrograms />} />
            <Route path="/programs/create" element={
              <ProtectedRoute canAccess={canCreateProgram}>
                <CreateProgram />
              </ProtectedRoute>
            } />
            <Route path="/programs/:programId/edit" element={
              <ProtectedRoute canAccess={canCreateProgram}>
                <CreateProgram />
              </ProtectedRoute>
            } />
            <Route path="/programs/:programId" element={<ProgramDetails />} />
            <Route path="/programs/:programId/cohorts/create" element={<CohortSettings />} />
            <Route path="/cohorts/:cohortId/settings" element={<CohortSettings />} />
            <Route path="/cohorts/:cohortId" element={<CohortDetails />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        }
      />
    </>
  );
};

/**
 * Auth Gate — checks authentication and shows session expired screen if needed
 */
const AuthGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isSessionExpired, isLoading, error, checkAuth, login } = useAuthStore();

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (!isAuthenticated && !isSessionExpired && !isLoading && !error) {
      login();
    }
  }, [isAuthenticated, isSessionExpired, isLoading, error, login]);

  if (isLoading) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 's' }}>
          Authenticating...
        </Box>
      </Box>
    );
  }

  if (isSessionExpired) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <SpaceBetween size="m" alignItems="center">
          <Box variant="h2">Your session has expired</Box>
          <Box variant="p" color="text-body-secondary">
            You've been signed out due to inactivity. Sign in again to continue.
          </Box>
          <Button variant="primary" onClick={login}>
            Sign in
          </Button>
        </SpaceBetween>
      </Box>
    );
  }

  if (error) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Box variant="h2">Authentication Error</Box>
        <Box variant="p" padding={{ top: 's' }}>
          {error}
        </Box>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <Spinner size="large" />
        <Box variant="p" padding={{ top: 's' }}>
          Redirecting to sign in...
        </Box>
      </Box>
    );
  }

  return <>{children}</>;
};

/**
 * Main App Component
 */
const App: React.FC = () => {
  if (isMaintenanceMode()) {
    return (
      <Box textAlign="center" padding={{ top: 'xxxl' }}>
        <SpaceBetween size="l" alignItems="center">
          <Box variant="h1">Cohort Manager</Box>
          <Box variant="h2">Coming Soon</Box>
          <Box variant="p" color="text-body-secondary">
            This application is currently in development and not yet available for general use.
          </Box>
          <Box variant="p" color="text-body-secondary">
            For more information, visit the{' '}
            <a
              href="https://w.amazon.com/bin/view/Users/mmssym/CohortManager/"
              style={{ color: 'inherit' }}
            >
              program wiki
            </a>
            .
          </Box>
        </SpaceBetween>
      </Box>
    );
  }

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <AuthGate>
            <BreadcrumbProvider>
              <Router>
                <AppContent />
              </Router>
            </BreadcrumbProvider>
          </AuthGate>
        </NotificationProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
